import GoogleAnalytics from '@disco/api/ga'
import mixpanel from '@disco/api/mixpanel'
import localApi, { RemoteApi } from '@disco/api/server'
import {
  ADD_TO_DISCO,
  ADD_TO_DISCO_CHOOSE_BUSINESS,
  ADD_TO_DISCO_CHOOSE_CANCEL,
  ADD_TO_DISCO_ERROR,
  ADD_TO_DISCO_SUCCESS,
  LOAD_PLAYLIST_PAGE_SUCCESS,
  USER_LOGIN_REQUIRE_MFA,
  USER_LOGIN_SUCCESS,
} from '@disco/constants/ActionTypes'
import businessCookie from '@disco/utils/business-cookie'
import { pushUINotification } from '@disco/utils/notifications'
import omit from 'lodash/omit'
import type { ClientLibraryId } from 'types/client-library'
import type { PlaylistId } from 'types/playlist'
import type { AddToDiscoObject, PublicPlaylist } from 'types/public-playlist'
import type { GetState } from 'types/redux'
import type { TrackFormatSelection } from 'types/track'
import type { LoginDetails } from 'types/user'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import { getLoginErrorFromServerResponse } from '@disco/state-management/actions/UserActions'
/**
 * Loads a public playlist page into the store and marks it as successfully loaded.
 * @param playlist - The public playlist to load
 * @returns Thunk that loads the playlist and dispatches success action
 */
export function loadPlaylistPage(
  playlist: PublicPlaylist,
): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch(loadBatch([playlist]))
    dispatch({
      type: LOAD_PLAYLIST_PAGE_SUCCESS,
      playlist,
    })
  }
}

function serializeObject(object: AddToDiscoObject): {
  file_formats?: Array<TrackFormatSelection>
  playlist_id?: PlaylistId
  include_nested_tracks?: boolean
  include_stems?: boolean
} {
  switch (object.type) {
    case 'PLAYLIST':
      return {
        include_nested_tracks: object.include_nested_tracks,
        include_stems: object.include_stems,
      }

    case 'SELECTION':
      return {
        file_formats: object.ids,
      }

    case 'TRACK':
      return {
        file_formats: Object.keys(object.track.all_files || {}).map(f => ({
          track_file_id: object.track.all_files?.[f]?.id,
          format: f,
        })),
      }

    case 'SECTION':
      return {
        file_formats: object.ids,
      }

    default:
      return {}
  }
}

function loginUserLightweight(api, loginDetails: LoginDetails, context) {
  return async dispatch => {
    try {
      const userAuthResp = await api.postUserAuth(loginDetails)

      if (userAuthResp.status !== 'success') {
        if (userAuthResp.reason === 'mfa_required') {
          return dispatch({
            type: USER_LOGIN_REQUIRE_MFA,
            mfaType: userAuthResp.mfa_type,
          })
        }

        const { message, code } = getLoginErrorFromServerResponse(userAuthResp)
        return dispatch({
          type: ADD_TO_DISCO_ERROR,
          error: {
            code,
            message,
            providedLoginDetails: true,
          },
          ...context,
        })
      }

      const userObj = {
        // nothing is needed
        csrfToken: api.csrfToken,
      }
      dispatch({
        type: USER_LOGIN_SUCCESS,
        result: userObj,
      })
    } catch (err) {
      dispatch(sendGenericError(err))
      return dispatch({
        type: ADD_TO_DISCO_ERROR,
        error: {
          providedLoginDetails: true,
          message: err.toString(),
        },
        ...context,
      })
    }
  }
}

/**
 * Generates a unique key for an AddToDiscoObject based on its type and ID.
 * @param object - The AddToDisco object to generate a key for
 * @returns Unique string key for the object or undefined if invalid type
 */
export function objectKey(object: AddToDiscoObject): void | string {
  switch (object.type) {
    case 'PLAYLIST':
      return `playlist-${object.playlist_id}`

    case 'SELECTION':
      // objectKey="selection" make selection saveable only once
      return `selection`

    case 'TRACK':
      return `track-${object.track.id}`

    case 'SECTION':
      return `section-${object.section.id}`
  }
}
/**
 * Gets the signed URL for the current public page, handling localhost development.
 * @returns The signed URL for the current page
 */
export function getSignedPublicPageUrl(): any | string {
  if (window.location.hostname.includes('localhost')) {
    // workaround for Save to DISCO on localhost
    return (
      'https://' +
      window.business_data.hostname +
      window.location.pathname +
      window.location.search
    )
  }

  return window.location.href
}

type BusinessFromApi = {
  id: number
  name: string
  url: string
  hostname?: string
}

/**
 * Normalizes business URLs for the anonymous login flow.
 * Used only for getBusinessesAnonymously() which returns { id, name, url, hostname }.
 * API always returns full URLs (with protocol), so bare hostnames are not expected.
 */
function normalizeBusinessList(businesses: BusinessFromApi[]) {
  return businesses.flatMap(business => {
    if (!business.url) {
      console.warn(
        '[normalizeBusinessList] Dropping business without URL:',
        business.name || business.id,
      )
      return []
    }

    let urlObj: URL
    try {
      urlObj = new URL(business.url)
    } catch {
      console.warn(
        '[normalizeBusinessList] Invalid URL for business:',
        business.name || business.id,
        business.url,
      )
      return []
    }

    const hostname = business.hostname || urlObj.hostname
    const subdomain = hostname.split('.')[0]

    // Convert localhost and PR environment URLs to staging URLs
    // PR URLs look like: au.discover--branch-name.pr.dsc.party
    // These don't have real backends, so we route to staging instead
    if (
      window.location.hostname.includes('localhost') ||
      hostname.includes('.pr.dsc.party')
    ) {
      return [
        {
          ...business,
          url: `https://${subdomain}.disco-staging-2.com`,
          hostname: subdomain,
        },
      ]
    }

    // For production/staging environments, use the hostname as-is
    return [
      {
        ...business,
        url: `${urlObj.protocol}//${hostname}`,
      },
    ]
  })
}

/**
 * Adds content (tracks, playlists, selections) to a user's Disco business account.
 * Handles authentication, business selection, and various object types.
 * @param business - Target business to add content to (null to show chooser)
 * @param url - The URL of the content being added
 * @param object - The object being added (track, playlist, selection, etc.)
 * @param loginDetails - Optional login credentials for authentication
 * @param context - Additional context like playlist/share/campaign IDs
 * @param mixpanelEventDetails - Optional analytics event details
 * @returns Thunk that handles the complete add-to-disco flow
 */
export function addToDisco(
  business:
    | {
        url: string
        name: string
        id: number
      }
    | null
    | undefined,
  url: string,
  object: AddToDiscoObject,
  loginDetails?: LoginDetails,
  context?: {
    playlistId?: number | null | undefined
    shareId?: number | null | undefined
    campaignId?: number | null | undefined
    recipientId?: number | null | undefined
    clientLibraryId?: ClientLibraryId | null | undefined
    showModalSuccessDialog?: boolean
  },
  mixpanelEventDetails?: Record<string, any>,
): (dispatch: any, getState: GetState) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    let destBusiness
    let loginDetailsFinal = loginDetails

    try {
      const remoteApi = new RemoteApi()
      dispatch({
        type: ADD_TO_DISCO,
        destBusiness: business,
        objectKey: objectKey(object),
      })
      GoogleAnalytics.sendEvent(
        'Add to DISCO',
        `Add to DISCO initiated (${object.type.toLowerCase()})`,
      )

      if (!business) {
        let businesses

        if (!context?.clientLibraryId && loginDetailsFinal) {
          // logging in to get list of businesses
          const respBusinesses = await localApi.getBusinessesAnonymously(
            loginDetailsFinal,
          )
          businesses = normalizeBusinessList(
            respBusinesses as BusinessFromApi[],
          )

          if (businesses.length === 0) {
            let code

            if (respBusinesses.length > 0) {
              code = 'ADD_TO_DISCO_ERROR_ALREADY_ADDED'
            } else {
              code = 'ADD_TO_DISCO_ERROR_NO_DISCOS'
            }

            return dispatch({
              type: ADD_TO_DISCO_ERROR,
              destBusiness: business,
              object: object,
              mixpanelEventDetails,
              objectKey: objectKey(object),
              error: {
                providedLoginDetails: true,
                code: code,
              },
            })
          }
        } else {
          // clicked 'add to disco', get cached business list
          businesses = businessCookie()

          if (businesses.length === 0) {
            // no cookie yet / empty list - ask to login to get the list
            return dispatch({
              type: ADD_TO_DISCO_CHOOSE_BUSINESS,
              objectKey: objectKey(object),
              businesses: [],
              object,
              mixpanelEventDetails,
            })
          }
        }

        if (businesses.length === 1) {
          // common case - one business only
          destBusiness = businesses[0]
        } else {
          // less common case - 2+ businesses, ask which one
          return dispatch({
            type: ADD_TO_DISCO_CHOOSE_BUSINESS,
            objectKey: objectKey(object),
            loginDetails: loginDetailsFinal,
            businesses,
            object,
            mixpanelEventDetails,
          })
        }
      } else {
        // the business was explicitly chosen from a list
        destBusiness = business
      }

      if (context?.clientLibraryId) {
        if (process.env.NEXT_PUBLIC_IS_CUSTOM_DOMAIN === 'true') {
          // TODO: rework this
          remoteApi.host = // '/_b/h='
            window.host + '/h=' + destBusiness.url.replace('https://', '')
        } else {
          remoteApi.host = // '/_b/h='
            window.host + '/_b/h=' + destBusiness.url.replace('https://', '')
        }

        remoteApi.csrfToken = localApi.csrfToken
      } else {
        const { hostname } = window.location
        // In dev all hosts are underneath disco-staging-2.com instead of the default business URL
        if (
          hostname.includes('localhost') &&
          !destBusiness.url.includes('disco-staging')
        ) {
          remoteApi.host = `${destBusiness.url}.disco-staging-2.com`
        } else {
          remoteApi.host = destBusiness.url
        }
        remoteApi.csrfToken = await remoteApi.getCSRFToken()
      }

      const { addToDiscoLoginDetails } = getState().user
      // assume that server invalid login count is single value for all
      // businesses and after successfuly authenticating on business we
      // don't need to pass captcha, if we need, we need new captcha token
      // which is not implemented
      // addToDiscoLoginDetails is Record<string,any> but holds a LoginDetails stored earlier
      loginDetailsFinal = ((addToDiscoLoginDetails &&
        omit(addToDiscoLoginDetails, 'captcha')) ||
        loginDetails) as LoginDetails | undefined

      if (loginDetailsFinal && loginDetails && 'mfa_code' in loginDetails) {
        loginDetailsFinal = {
          ...loginDetailsFinal,
          mfa_code: (loginDetails as { mfa_code?: unknown }).mfa_code,
        } as LoginDetails
      }

      if (!context?.clientLibraryId && loginDetailsFinal) {
        await dispatch(
          loginUserLightweight(remoteApi, loginDetailsFinal, {
            destBusiness: destBusiness,
            object: object,
            objectKey: objectKey(object),
          }),
        )
        if (!getState().user.loggedIn) return
        // refresh csrfToken after login, otherwise add_to_disco will throw 403
        remoteApi.csrfToken = await remoteApi.getCSRFToken()
      } else if (!context?.clientLibraryId) {
        // check session - if it fails with 403 or user id is empty we ask to login
        const userDetails = await remoteApi.getUserDetails()

        if (!userDetails?.id) {
          const error: Record<string, any> = new Error('Unauthorized')
          error.status = 403
          throw error
        }
      }

      // whenever possible pass playlist_id as share can be channel,
      // not only playilst
      const extra = serializeObject(object)

      if (context?.playlistId) {
        extra.playlist_id = context.playlistId
      } else if ('playlist_id' in object) {
        extra.playlist_id = Number(object.playlist_id)
      }

      let result = {
        redirect_url: null,
      }

      if ('token' in object) {
        result = await remoteApi.saveToDiscoCustomer(object.token)
      } else {
        await remoteApi.addToDisco(url, extra, context?.clientLibraryId)
      }

      dispatch({
        type: ADD_TO_DISCO_SUCCESS,
        destBusiness: destBusiness,
        showModalSuccessDialog:
          object.type === 'PLAYLIST' ||
          object.type === 'SECTION' ||
          object.type === 'SELECTION' ||
          context?.showModalSuccessDialog,
        objectKey: objectKey(object),
        redirect_url: result.redirect_url,
      })
      GoogleAnalytics.sendEvent(
        'Add to DISCO',
        `Add to DISCO completed (${object.type.toLowerCase()})`,
      )

      // For playlist, section or selection we send a playlist download event to the source
      // business for analytics tracking (e.g., campaign performance, share link engagement).
      // See: https://app.asana.com/0/71370194060529/768711000509229
      //
      // Skip for discover page saves (clientLibraryId < 0):
      // - The playlist download event is sent to localApi which points to the current page's domain
      // - For discover page, localApi points to the discover domain, not the source business
      // - The playlistId belongs to the source business and doesn't exist on the discover domain
      // - To fix this, we'd need to route via /_b/h= proxy to the source business, but
      //   /api/playlist/*/event/ is not in the Lambda@Edge allowlist
      // - Analytics for discover saves are tracked via Mixpanel instead (see mixpanelEventDetails)
      const isDiscoverPageSave =
        context?.clientLibraryId && context.clientLibraryId < 0
      if (
        object.type !== 'TRACK' &&
        context?.playlistId &&
        !isDiscoverPageSave
      ) {
        localApi.sendPlaylistDownloadEvent(
          context.playlistId,
          context.shareId,
          undefined,
          context.campaignId,
          context.recipientId,
          context.clientLibraryId,
        )
      }

      if (object.type === 'TRACK' && context) {
        localApi.sendTrackDownloadEvent(
          object.track.id,
          'public-playlist-page',
          {
            playlist_id: context.playlistId,
            share_id: context.shareId,
            campaign_id: context.campaignId,
            recipient_id: context.recipientId,
            client_library_id: context.clientLibraryId,
          },
        )
        pushUINotification({
          message: `Success! Added to ${destBusiness.name || 'business'}.`,
          level: 'success',
        })
      }

      if (mixpanelEventDetails) {
        mixpanel.sendPureEvent('Save to DISCO', mixpanelEventDetails)
      }
    } catch (err) {
      const unauthorized = err.status === 403

      if (!unauthorized) {
        pushUINotification({
          message: err.message,
          level: 'error',
        })
      }

      let code: string | null = null
      let message = err.message
      if (unauthorized) code = 'ADD_TO_DISCO_ERROR_UNAUTHORIZED'

      if (err.body?.reason) {
        const error = getLoginErrorFromServerResponse(err.body)
        code = error.code
        message = error.message
      } else if (err.status === 400 && err.body?.message) {
        message = err.body.message
      }

      dispatch(sendGenericError(err))
      dispatch({
        type: ADD_TO_DISCO_ERROR,
        destBusiness: destBusiness,
        object: object,
        mixpanelEventDetails,
        objectKey: objectKey(object),
        error: {
          status: err.status,
          providedLoginDetails: Boolean(loginDetailsFinal),
          code,
          destBusiness: destBusiness,
          message,
        },
      })
    }
  }
}
/**
 * Cancels the add-to-disco flow and closes any open dialogs.
 * @returns Action object to cancel the add-to-disco process
 */
export function addToDiscoCancel(): {
  type: string
} {
  return {
    type: ADD_TO_DISCO_CHOOSE_CANCEL,
  }
}
